// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CachedWorkflowStageCascadeListProperty } from '@/audit/api/types';
import { Box, Typography } from '@mui/material';

interface Props {
  /**
   * The workflow stage cascade list property to render.
   */
  property: CachedWorkflowStageCascadeListProperty;
  /**
   * The name of the project that was affected by the cascade.
   */
  projectName?: string;
}

type WorkflowStageCascadeItem = NonNullable<
  CachedWorkflowStageCascadeListProperty['newValue']
>[0];

/**
 * Compares two cascade stage arrays and returns whether they are different
 */
function cascadeStageListsAreDifferent(
  oldStages: WorkflowStageCascadeItem[] | null | undefined,
  newStages: WorkflowStageCascadeItem[] | null
): boolean {
  if (!oldStages && !newStages) {
    return false;
  }

  if (!oldStages || !newStages) {
    return true;
  }

  if (oldStages.length !== newStages.length) {
    return true;
  }

  // Sort both arrays by orderIndex to ensure consistent comparison
  const sortedOld = [...oldStages].sort((a, b) => a.orderIndex - b.orderIndex);
  const sortedNew = [...newStages].sort((a, b) => a.orderIndex - b.orderIndex);

  // Check if any stage properties changed
  for (let i = 0; i < sortedOld.length; i++) {
    const oldStage = sortedOld[i];
    const newStage = sortedNew[i];

    if (
      oldStage.id !== newStage.id ||
      oldStage.name !== newStage.name ||
      oldStage.orderIndex !== newStage.orderIndex ||
      oldStage.key !== newStage.key ||
      oldStage.description !== newStage.description ||
      oldStage.shouldHighlight !== newStage.shouldHighlight
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Formats a cascade stage list for display, with project-specific annotations
 */
function formatCascadeStageList(
  stages: WorkflowStageCascadeItem[],
  projectName?: string,
  isOldList = false
): string {
  return stages
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((stage) => {
      let annotation = '';
      if (stage.shouldHighlight && projectName) {
        if (isOldList) {
          annotation = ` ← project ${projectName} was in this stage`;
        } else {
          annotation = ` ← project ${projectName} is now in this stage`;
        }
      }
      return `   ${stage.orderIndex}) ${stage.name}${annotation}`;
    })
    .join('\n');
}

/**
 * A component that renders workflow stage cascade list changes, showing the full stage comparison
 * with highlighting for changed stages.
 */
export function AuditWorkflowStageCascadeList(props: Props) {
  const hasChanges = cascadeStageListsAreDifferent(
    props.property.oldValue,
    props.property.newValue
  );

  // If no changes detected, don't render anything
  if (!hasChanges || !props.property.oldValue || !props.property.newValue) {
    return null;
  }

  const previousStages = formatCascadeStageList(
    props.property.oldValue,
    props.projectName,
    true
  );
  const currentStages = formatCascadeStageList(
    props.property.newValue,
    props.projectName,
    false
  );

  return (
    <Box>
      <Typography variant="caption" component="div" sx={{ fontWeight: 'bold' }}>
        Stages:
      </Typography>
      <Box sx={{ ml: 1 }}>
        <Typography
          variant="caption"
          component="div"
          sx={{ fontWeight: 'bold' }}
        >
          Previous:
        </Typography>
        <Typography
          variant="caption"
          component="pre"
          sx={{
            fontFamily: 'inherit',
            whiteSpace: 'pre-wrap',
          }}
        >
          {previousStages}
        </Typography>

        <Typography
          variant="caption"
          component="div"
          sx={{ fontWeight: 'bold' }}
        >
          Current:
        </Typography>
        <Typography
          variant="caption"
          component="pre"
          sx={{
            fontFamily: 'inherit',
            whiteSpace: 'pre-wrap',
          }}
        >
          {currentStages}
        </Typography>
      </Box>
    </Box>
  );
}
